import * as React from 'react';
import { Layout, Row, Col, Card, Button, Divider, message, Steps, Popover } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import {
	downloadInvoicePDFForSupplier,
	invoicesRestCrudThunksForSupplier,
	markInvoiceDraftForSupplier,
	markInvoicePaidForSupplier,
	publishInvoiceForSupplier,
} from '../../thunks/invoices_thunks';
import { dateFormatter, getTimeDurationFromSeconds } from '../../utils/DataFormatterUtils';
import { getCurrency, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import LogOnMountWithStandardEventProperties from '../log_on_mount_with_standard_event_properties/LogOnMountWithStandardEventProperties';
import { INVOICE_STATUSES } from '../../constants/InvoiceStatuses';
import InvoiceUploader from '../invoice_uploader/InvoiceUploader';
import {
	appendWorkOrderNotesForSupplier,
	workOrdersRestCrudThunksForSupplier,
} from '../../thunks/work_orders_thunks';
import InvoicesAdditionalDetails from '../invoices_components/InvoicesAdditionalDetails';
import { SUPER_ADMIN, SUPPLIER_ADMIN } from '../roles/roles';
import { markUserNotificationsReadForSupplier } from '../../thunks/user_notifications_thunks';
import { UndoOutlined } from '@ant-design/icons';
import { UserName } from '../name_component/user/UserNameComponent';
import moment from 'moment';
import InvoiceActions from '../common/InvoiceActions';
import DateTimeHover from '../date_time_component/DateTime';

const { Content } = Layout;

const style = require('./SupplierInvoicesDetailDetailsPage.less');

const dateFormat = 'MMM D, YYYY';

class InvoicesDetailPage extends React.Component<any, any> {
	constructor(props) {
		super(props);
		this.state = {
			generatingPDF: false,
			pdfFile: { url: null, httpHeaders: null },
			buttonLoading: false,
		};
	}

	componentDidMount() {
		const { getWorkOrder, getInvoice, match, markUserNotificationsRead } = this.props;
		const invoiceId = match.params.id;
		getInvoice(invoiceId).then((invoice) => {
			getWorkOrder(invoice.workOrderId);
			markUserNotificationsRead(invoice.id);
		});
	}

	refreshInvoice = () => {
		const { getWorkOrder, getInvoice, match } = this.props;
		const invoiceId = match.params.id;
		return getInvoice(invoiceId).then((invoice) => {
			getWorkOrder(invoice.workOrderId);
			return invoice;
		});
	};

	handlePublishInvoice = () => {
		const { publishInvoice, invoice } = this.props;
		publishInvoice(invoice.id).then(() => {
			message.success(`Invoice published.`);
		});
	};

	handlePaidInvoice = () => {
		const { markInvoicePaid, invoice } = this.props;
		this.setState({ buttonLoading: true });
		markInvoicePaid(invoice.id)
			.then(() => {
				message.success(`Invoice paid.`);
			})
			.finally(() => this.setState({ buttonLoading: false }));
	};

	handleFileAttachmentUploadChange = (newAttachments) => {
		const { updateInvoice, invoice } = this.props;
		const newInvoice = {
			...invoice,
			attachments: newAttachments,
		};
		updateInvoice(newInvoice);
	};

	handleInvoicePDFUploadChange = (invoiceFileUrl) => {
		const { updateInvoice, getInvoice, invoice } = this.props;

		const newInvoice = {
			...invoice,
			invoicePDFLink: invoiceFileUrl,
		};

		updateInvoice(newInvoice).then(() => {
			getInvoice(invoice.id);
		});
	};

	generateInvoicePDF(id) {
		this.setState({ generatingPDF: true });
		downloadInvoicePDFForSupplier(id).finally(() => {
			this.setState({ generatingPDF: false });
		});
	}

	handleMoveInvoiceToDraft = () => {
		const { markInvoiceDraft, invoice } = this.props;
		this.setState({ buttonLoading: true });
		markInvoiceDraft(invoice.id)
			.then(() => {
				message.success(`Invoice moved to draft.`);
			})
			.finally(() => this.setState({ buttonLoading: false }));
	};

	handleInvoicePDFUploadChangeForDeletedInvoice = (invoiceFileUrl) => {
		const { updateInvoice, getInvoice, invoice } = this.props;

		const newInvoice = {
			...invoice,
			invoicePDFLink: invoiceFileUrl,
			isDeleted: false,
		};

		updateInvoice(newInvoice).then(() => {
			getInvoice(invoice.id);
		});
	};

	render() {
		const {
			workOrder,
			workOrdersFetching,
			invoice,
			invoicesFetching,
			currentUser,
			history,
			invoicesCreating,
			appendWorkOrderNotes,
		} = this.props;
		const currency = getCurrency({ workOrder: workOrder });
		const isInternalTech = nullSafeGetOrElse('facility.status', currentUser, '') === 'private';
		const statuses = ['draft', 'pending', 'approved', 'processing', 'paid'];
		const invoiceIndex = statuses.indexOf(invoice.status);
		const isBuyerInvoice = invoice.isBuyerUploaded || false;

		const nte = nullSafeGet('workOrder.nte', invoice);
		const total = nullSafeGet('invoiceTotalAfterTax', invoice);
		const invoiceId = nullSafeGet('id', invoice);
		const totalIsGreaterThanNTE = nte < total;

		const isAdmin =
			currentUser.roles.includes(SUPPLIER_ADMIN) || currentUser.roles.includes(SUPER_ADMIN);
		const hasPDF = invoice.invoicePDFLink && invoice.invoicePDFLink.length > 0;
		const pdfElement = hasPDF ? (
			<p className={'mb-1'}>
				<a href={invoice.invoicePDFLink} target="_blank" rel="noreferrer">
					Invoice PDF
				</a>
			</p>
		) : null;
		const uploadInvoice = isAdmin ? (
			[INVOICE_STATUSES.draft, INVOICE_STATUSES.disputed].some(
				(status) => status === invoice.status
			) ? (
				<InvoiceUploader
					roleType="buyer"
					invoiceId={invoice.id}
					defaultFileList={[]}
					multiple={false}
					handleUploadSuccess={this.handleInvoicePDFUploadChange}
				/>
			) : invoice.status === INVOICE_STATUSES.pending ? (
				<Popover
					content={
						isInternalTech
							? 'Invoices must be disputed status to allow upload of a pdf.'
							: 'Invoice must be moved back to draft status to allow upload of a pdf.'
					}
					placement={'left'}
				>
					<div>
						<InvoiceUploader
							disabled={true}
							roleType="buyer"
							invoiceId={invoice.id}
							defaultFileList={[]}
							multiple={false}
							handleUploadSuccess={this.handleInvoicePDFUploadChange}
						/>
					</div>
				</Popover>
			) : null
		) : null;
		const generateInvoice = !isInternalTech ? (
			<p style={{ marginBottom: 0, marginTop: 0 }}>
				Don't have an invoice file?{' '}
				<Button
					loading={this.state.generatingPDF}
					type="link"
					onClick={() => this.generateInvoicePDF(invoice.id)}
					style={{ marginBottom: 0, marginTop: 0 }}
				>
					Generate PDF
				</Button>
			</p>
		) : null;

		const lastDisputeNote = invoice.disputeNotes && invoice.disputeNotes.slice(-1)[0];
		const disputeNote = lastDisputeNote ? (
			<Col>
				<div>
					<span style={{ fontSize: 16 }}>
						Disputed by{' '}
						<UserName
							mode={'inline'}
							contact={nullSafeGet(`noteAddedByContact`, lastDisputeNote)}
						/>{' '}
						{moment(nullSafeGet(`noteAddedAt`, lastDisputeNote)).fromNow()}
					</span>
				</div>
				<div
					style={{
						display: 'inline-flex',
						background: '#fafafa',
						color: 'rgba(0, 0, 0, 0.65)',
						borderRadius: '30px',
						padding: '8px 16px',
					}}
				>
					<span
						style={{
							fontSize: 16,
						}}
					>
						{nullSafeGet(`text`, lastDisputeNote)}
					</span>
				</div>
				{!isInternalTech ? (
					<div className={'mt-2 flex gap-4'}>
						<Button
							type="primary"
							key="Edit"
							onClick={() => history.push(`/supplier/invoices/edit/${invoiceId}`)}
						>
							Edit Invoice
						</Button>
						<Button
							key="Note"
							onClick={() =>
								history.push(`/supplier/workOrders/detail/${invoice.workOrderId}/notes`)
							}
						>
							Send a note
						</Button>
					</div>
				) : null}
			</Col>
		) : null;

		if (invoice.isDeleted) {
			return (
				<Row gutter={12}>
					<Col span={24}>
						<Card>
							<div>
								<h5 style={{ marginBottom: '4px' }}>Upload an invoice file</h5>
								<div>
									<p
										style={{
											fontSize: 16,
											marginBottom: '0.5em',
										}}
									>
										Add an invoice file (PDF) to finish creating the invoice.
									</p>
									<InvoiceUploader
										roleType="supplier"
										invoiceId={invoice.id}
										buttonType="primary"
										defaultFileList={[]}
										multiple={false}
										handleUploadSuccess={this.handleInvoicePDFUploadChangeForDeletedInvoice}
									/>
								</div>
							</div>
						</Card>
					</Col>
				</Row>
			);
		}

		return invoicesFetching || workOrdersFetching ? (
			<PageLoadingPlaceholder />
		) : (
			<Content className="invoicesDetailDetailsPage">
				<LogOnMountWithStandardEventProperties eventType="visited supplier invoice detail page" />
				{/*<ScrollToTopOnMount/>*/}
				{/*<BackTop/>*/}
				<Row style={{ margin: '16px 4px' }} gutter={12}>
					<Col span={24}>
						<Card>
							<InvoiceActions
								invoice={invoice}
								refreshInvoice={this.refreshInvoice}
								updateInvoice={this.props.updateInvoice}
								style={style}
								appendWorkOrderNotes={appendWorkOrderNotes}
							/>
							{!isInternalTech &&
							invoice.status === 'draft' &&
							(!invoice.invoicePDFLink || invoice.invoicePDFLink.length === 0) ? (
								<div className="rowSpacing">
									<Card
										style={{ border: '1px solid #8ed7eb' }}
										bodyStyle={{
											padding: '16px 24px',
											background: '#ebfbff',
										}}
									>
										<div>
											<h5 style={{ marginBottom: '4px' }}>Upload an invoice file</h5>
											<div>
												<p
													style={{
														fontSize: 16,
														marginBottom: '0.5em',
													}}
												>
													Please add an invoice file (PDF) for this invoice before submitting it.
												</p>
												<InvoiceUploader
													roleType="supplier"
													invoiceId={invoice.id}
													buttonType="primary"
													defaultFileList={[]}
													multiple={false}
													handleUploadSuccess={this.handleInvoicePDFUploadChange}
												/>
												<p style={{ marginBottom: 0, marginTop: 8 }}>
													Don't have an invoice file?{' '}
													<Button
														loading={this.state.generatingPDF}
														type="link"
														onClick={() => this.generateInvoicePDF(invoice.id)}
													>
														Generate PDF
													</Button>
												</p>
											</div>
										</div>
									</Card>
								</div>
							) : null}
							<InvoicesAdditionalDetails
								invoice={invoice}
								passthroughItems={[
									pdfElement || uploadInvoice
										? {
												key: 'File',
												value: (
													<div>
														{pdfElement}
														{uploadInvoice}
														{generateInvoice}
													</div>
												),
												className: 'detailRow__item firstRow lastCol',
										  }
										: {},
									{
										key: 'Submitted By Company',
										value: isBuyerInvoice
											? nullSafeGet('buyerCompany.displayName', workOrder)
											: nullSafeGet('supplierFacility.displayName', workOrder),
									},
									{
										key: 'Submitted On',
										value: <DateTimeHover timestamp={nullSafeGet('createdAt', invoice)} />,
									},
								]}
							/>

							<Divider />

							<div className="sectionHeaderWrapper" style={{ marginBottom: 8 }}>
								<span className="sectionHeaderText">Labor</span>
								<span className="sectionHeaderText2">
									{currency.format(invoice.laborTotalBeforeTax)}
								</span>
							</div>
							{nullSafeGetOrElse('laborLineItems', invoice, []).map((lli, idx) => (
								<div
									className="sectionHeaderWrapper2"
									style={{
										marginBottom:
											idx === nullSafeGetOrElse('laborLineItems', invoice, []).length - 1 ? 16 : 8,
									}}
								>
									<span className="lineItemDetailText">
										{lli.numberOfTech} {lli.roleType} * {getTimeDurationFromSeconds(lli.seconds)} @{' '}
										{currency.format(parseFloat(lli.hourlyRate))} / hour
									</span>
									<span className="lineItemDetailText">
										{currency.format(parseFloat(lli.amount))}
									</span>
								</div>
							))}
							<Divider />

							<div className="sectionHeaderWrapper" style={{ marginBottom: 8 }}>
								<span className="sectionHeaderText">Materials</span>
								<span className="sectionHeaderText2">
									{currency.format(invoice.materialTotalBeforeTax)}
								</span>
							</div>
							{nullSafeGetOrElse('materialLineItems', invoice, []).map((mli, idx) => (
								<div
									className="sectionHeaderWrapper2"
									style={{
										marginBottom:
											idx === nullSafeGetOrElse('laborLineItems', invoice, []).length - 1 ? 16 : 8,
									}}
								>
									<span className="lineItemDetailText">
										{mli.quantity} {mli.unitType} of {mli.description}
										{mli.partNumber ? ` (${mli.partNumber})` : null} @{' '}
										{currency.format(parseFloat(mli.unitPrice))} / {mli.unitType}
									</span>
									<span className="lineItemDetailText">
										{currency.format(parseFloat(mli.amount))}
									</span>
								</div>
							))}
							<Divider />
							<div className="sectionHeaderWrapper">
								<span className="sectionHeaderText">Travel</span>
								<span className="sectionHeaderText2">
									{currency.format(parseFloat(invoice.travelTotalBeforeTax) || 0)}
								</span>
							</div>
							<Divider />
							<div className="sectionHeaderWrapper">
								<span className="sectionHeaderText">Freight</span>
								<span className="sectionHeaderText2">
									{currency.format(parseFloat(invoice.freightTotalBeforeTax) || 0)}
								</span>
							</div>
							<Divider />
							<div className="sectionHeaderWrapper">
								<span className="sectionHeaderText">Miscellaneous</span>
								<span className="sectionHeaderText2">
									{currency.format(parseFloat(invoice.miscTotalBeforeTax) || 0)}
								</span>
							</div>
							{nullSafeGetOrElse('miscLineItems', invoice, []).map((mli, idx) => (
								<div
									className="sectionHeaderWrapper2"
									style={{
										marginBottom:
											idx === nullSafeGetOrElse('miscLineItems', invoice, []).length - 1 ? 16 : 8,
									}}
								>
									<span className="lineItemDetailText">{mli.description}</span>
									<span className="lineItemDetailText">
										{currency.format(parseFloat(mli.amount))}
									</span>
								</div>
							))}
							<Divider />
							<div className="sectionHeaderWrapper">
								<span className="sectionHeaderText">Total Before Tax</span>
								<span className="sectionHeaderText2">
									{currency.format(invoice.invoiceTotalBeforeTax)}
								</span>
							</div>
							<Divider />
							<div className="sectionHeaderWrapper">
								<span className="sectionHeaderText">Tax</span>
								<span className="sectionHeaderText2">{currency.format(invoice.invoiceTax)}</span>
							</div>
							{nullSafeGetOrElse('taxLineItems', invoice, []).map((lli, idx) => (
								<div
									className="sectionHeaderWrapper2"
									style={{
										marginBottom:
											idx === nullSafeGetOrElse('taxLineItems', invoice, []).length - 1 ? 16 : 8,
									}}
								>
									<span className="lineItemDetailText">{lli.taxType}</span>
									<span className="lineItemDetailText">
										{currency.format(parseFloat(lli.amount))}
									</span>
								</div>
							))}
							<Divider />
							<div className="sectionHeaderWrapper">
								<span className="sectionHeaderText" style={{ fontWeight: 600 }}>
									Total After Tax
								</span>
								<span className="sectionHeaderText2" style={{ fontWeight: 600 }}>
									{currency.format(invoice.invoiceTotalAfterTax)}
								</span>
							</div>
						</Card>
					</Col>
				</Row>
			</Content>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	workOrdersFetching: state.work_orders.fetching,
	workOrder: state.work_orders.detail,
	invoicesFetching: state.invoices.fetching,
	invoice: state.invoices.detail,
	invoicesCreating: state.invoices.creating,
	currentUser: state.session.currentUser,
	match: ownProps.match,
	history: ownProps.history,
});

const mapDispatchToProps = (dispatch) => ({
	appendWorkOrderNotes: (entity) => dispatch(appendWorkOrderNotesForSupplier(entity)),
	getWorkOrder: (id) => dispatch(workOrdersRestCrudThunksForSupplier.readOne(id)),
	getInvoice: (id) => dispatch(invoicesRestCrudThunksForSupplier.readOne(id)),
	updateInvoice: (entity) => dispatch(invoicesRestCrudThunksForSupplier.update(entity)),
	markInvoicePaid: (id) => dispatch(markInvoicePaidForSupplier({ id })),
	markInvoiceDraft: (id) => dispatch(markInvoiceDraftForSupplier({ id })),
	publishInvoice: (id) => dispatch(publishInvoiceForSupplier({ id })),
	markUserNotificationsRead: (entityId) =>
		dispatch(markUserNotificationsReadForSupplier('Invoice', entityId)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(InvoicesDetailPage));
